import { NutritionProgramWizard } from "@corpuscomplete/core-ui/nutrition/program/wizard";
import { BodyCharacteristic } from "@corpuscomplete/core/body/body-characteristic";
import { Button } from "@corpuscomplete/web-ui/components/button";
import {
  DialogHeader,
  DialogContent,
  Dialog,
  DialogFooter,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { Progress } from "@corpuscomplete/web-ui/components/progress";
import { NutrientTargetsLabel } from "@corpuscomplete/web/components/nutrition/nutrients-label";
import { FormErrors } from "@corpuscomplete/web/hooks/create-form-hook";
import { DateOfBirthInput } from "@corpuscomplete/web/components/body/date-of-birth-input";

export function ProgramWizardDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <NutritionProgramWizard.DataProvider>
      <Dialog
        open={open}
        size="lg"
        onOpenChange={(open) => {
          if (!open) {
            onClose();
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl">
          <NutritionProgramWizard.StoreProvider>
            <ProgramWizardDialogContent onClose={onClose} />
          </NutritionProgramWizard.StoreProvider>
        </DialogContent>
      </Dialog>
    </NutritionProgramWizard.DataProvider>
  );
}

function ProgramWizardDialogContent({ onClose }: { onClose: () => void }) {
  const chrome = NutritionProgramWizard.useWizardChrome();

  return (
    <>
      <DialogHeader className="flex flex-col gap-2">
        <DialogTitle className="font-semibold text-lg">{chrome.title}</DialogTitle>
        <Text variant="sm" className="text-muted-foreground">
          {chrome.description}
        </Text>
        <Progress value={chrome.progressValue} className="w-[85%] self-center" />
      </DialogHeader>
      <div className="flex flex-1 flex-col gap-2">
        <ProgramWizardContent />
      </div>
      <DialogFooter>
        {chrome.onBack && (
          <Button onClick={chrome.onBack} variant="ghost">
            <Text variant="sm">Back</Text>
          </Button>
        )}
        {chrome.onNext ? (
          <Button onClick={chrome.onNext}>
            <Text variant="sm">Next</Text>
          </Button>
        ) : (
          <Button
            disabled={!chrome.canSubmit}
            onClick={() => {
              chrome.onSubmit();
              if (chrome.canSubmit) {
                onClose();
              }
            }}
          >
            <Text variant="sm">Complete</Text>
          </Button>
        )}
      </DialogFooter>
    </>
  );
}

function ProgramWizardContent() {
  const chrome = NutritionProgramWizard.useWizardChrome();

  if (chrome.isLoading) {
    return null;
  }
  if (chrome.isComplete) {
    return <CompleteStep />;
  }
  switch (chrome.step) {
    case 0:
      return <BodyStep />;
    case 1:
      return <WeightGoalStep />;
    case 2:
      return <ActivityLevelStep />;
    case 3:
      return <DietStyleStep />;
    case 4:
      return <ProteinIntakeStep />;
    case 5:
      return <CompleteStep />;
    default:
      return null;
  }
}

function CompleteStep() {
  const step = NutritionProgramWizard.useCompleteStep();
  if (!step) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4">
      {step.nutrientTargets instanceof Error ? (
        <FormErrors errors={[step.nutrientTargets]} />
      ) : (
        <div className="flex flex-col gap-1">
          <Text variant="sm" className="font-medium">
            Daily targets
          </Text>
          <NutrientTargetsLabel targets={step.nutrientTargets} />
        </div>
      )}
    </div>
  );
}

function BodyStep() {
  const step = NutritionProgramWizard.useBodyStep();
  const form = step.form;

  return (
    <form.AppForm>
      {step.validators.dateOfBirth && (
        <form.AppField
          name="bodyCharacteristic.dateOfBirth"
          validators={{ onChange: step.validators.dateOfBirth }}
        >
          {(field) => (
            <DateOfBirthInput
              value={field.state.value}
              onBlur={field.handleBlur}
              errors={field.state.meta.errors}
              onChange={field.handleChange}
            />
          )}
        </form.AppField>
      )}

      {step.validators.biologicalSex && (
        <form.AppField
          name="bodyCharacteristic.biologicalSex"
          validators={{ onChange: step.validators.biologicalSex }}
        >
          {(field) => (
            <field.RadioGroup<BodyCharacteristic.BiologicalSex>
              label="Biological Sex"
              options={[
                { label: "Male", value: "male" },
                { label: "Female", value: "female" },
                { label: "Other", value: "other" },
              ]}
            />
          )}
        </form.AppField>
      )}

      {step.validators.height && (
        <form.AppField
          name="bodyComposition.height"
          validators={{ onChange: step.validators.height }}
        >
          {(field) => <field.Input label="Height" endText={step.heightUnitKey} />}
        </form.AppField>
      )}

      {step.validators.weight && (
        <form.AppField
          name="bodyComposition.weight"
          validators={{ onChange: step.validators.weight }}
        >
          {(field) => <field.Input label="Weight" endText={step.weightUnitKey} />}
        </form.AppField>
      )}

      {step.validators.leanBodyMass && (
        <form.AppField
          name="bodyComposition.leanBodyMass"
          validators={{ onChange: step.validators.leanBodyMass }}
        >
          {(field) => <field.Input label="Lean Body Mass" endText={step.weightUnitKey} />}
        </form.AppField>
      )}

      <form.Subscribe selector={(state) => state.errors}>
        {(errors) => <FormErrors errors={errors} />}
      </form.Subscribe>
    </form.AppForm>
  );
}

function WeightGoalStep() {
  const step = NutritionProgramWizard.useWeightGoalStep();
  const form = step.form;

  return (
    <form.AppForm>
      <form.AppField
        name="type"
        validators={{ onChange: NutritionProgramWizard.WEIGHT_GOAL_TYPE_VALIDATOR }}
      >
        {(field) => <field.RadioGroup options={step.options} />}
      </form.AppField>

      {step.type === "lose" || step.type === "gain" ? (
        <>
          <form.AppField
            name="targetWeight"
            validators={{ onChange: NutritionProgramWizard.WEIGHT_GOAL_NUMBER_VALIDATOR }}
          >
            {(field) => <field.Input label="Target weight" endText={step.weightUnitKey} />}
          </form.AppField>

          <form.AppField
            name="weightChangeRate"
            validators={{ onChange: NutritionProgramWizard.WEIGHT_GOAL_NUMBER_VALIDATOR }}
          >
            {(field) => (
              <field.Input
                label={`Weekly ${step.type === "lose" ? "loss" : "gain"} rate`}
                endText={`${step.weightUnitKey}/week`}
              />
            )}
          </form.AppField>

          <WeightGoalPreview />
        </>
      ) : null}
    </form.AppForm>
  );
}

function WeightGoalPreview() {
  const step = NutritionProgramWizard.useWeightGoalStep();
  const placeholder = "-";

  if (!step.preview || (step.type !== "lose" && step.type !== "gain")) {
    return null;
  }

  return (
    <div className="rounded-md bg-muted p-3 text-sm">
      <div className="flex justify-between">
        <span className="text-muted-foreground">
          Daily calorie {step.type === "lose" ? "deficit" : "surplus"}:
        </span>
        <span className="font-medium">
          {step.preview.calorieOffset !== null ? `${step.preview.calorieOffset} kcal` : placeholder}
        </span>
      </div>
      <div className="mt-1 flex justify-between">
        <span className="text-muted-foreground">Weight to {step.type}:</span>
        <span className="font-medium">
          {step.preview.weightDiff !== null
            ? `${step.preview.weightDiff.toFixed(1)} ${step.weightUnitKey}`
            : placeholder}
        </span>
      </div>
      <div className="mt-1 flex justify-between">
        <span className="text-muted-foreground">Expected completion:</span>
        <span className="font-medium">
          {step.preview.expectedDate !== null && step.preview.daysToGoal !== null
            ? `${step.preview.expectedDate} (${step.preview.daysToGoal} days)`
            : placeholder}
        </span>
      </div>
    </div>
  );
}

function ActivityLevelStep() {
  const step = NutritionProgramWizard.useActivityLevelStep();
  const form = step.form;
  return (
    <form.AppForm>
      <form.AppField name="value">
        {(field) => <field.RadioGroup options={step.options} />}
      </form.AppField>
    </form.AppForm>
  );
}

function DietStyleStep() {
  const step = NutritionProgramWizard.useDietStyleStep();
  const form = step.form;
  return (
    <form.AppForm>
      <form.AppField name="value">
        {(field) => <field.RadioGroup options={step.options} />}
      </form.AppField>
    </form.AppForm>
  );
}

function ProteinIntakeStep() {
  const step = NutritionProgramWizard.useProteinIntakeStep();
  const form = step.form;
  return (
    <form.AppForm>
      <form.AppField name="value">
        {(field) => <field.RadioGroup options={step.options} />}
      </form.AppField>
    </form.AppForm>
  );
}
